/**
 * Error Table Component
 *
 * Displays failed invitation email details. エラー内容 shows first sentence only;
 * unread rows are bold. Clicking a row opens a popup (handled by parent) and marks it read.
 */
import React from 'react';
import { FailedInvitation } from './types';
import './ErrorTable.css';

/** Returns the first sentence (up to first . or 。 or newline) or truncated text. */
export function getFirstSentence(text: string): string {
  if (!text || text.trim() === '') return '—';
  const t = text.trim();
  const period = t.search(/[。.]/);
  const newline = t.search(/\n/);
  let end = t.length;
  if (period >= 0) end = Math.min(end, period + 1);
  if (newline >= 0) end = Math.min(end, newline);
  const first = t.slice(0, end).trim();
  if (first.length < t.length && first.length > 0) return first;
  return t.length > 120 ? t.slice(0, 120) + '…' : t;
}

export interface ErrorTableProps {
  /** Failed invitation data to display */
  failedInvitations: FailedInvitation[];
  /** Called when a row is clicked (open popup and mark as read) */
  onRowClick?: (item: FailedInvitation) => void;
}

const ErrorTable: React.FC<ErrorTableProps> = ({ failedInvitations, onRowClick }) => (
  <div className="error-table">
    <div className="error-table__container">
      <table className="error-table__table">
        <thead>
          <tr>
            <th>名前</th>
            <th>メールアドレス</th>
            <th>エラー内容</th>
            <th>送信日時</th>
          </tr>
        </thead>
        <tbody>
          {failedInvitations.length > 0 ? (
            failedInvitations.map((failed) => (
              <tr
                key={failed.id}
                className={`error-table__row ${failed.isUnread ? 'error-table__row--unread' : ''}`}
                onClick={() => onRowClick?.(failed)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick?.(failed);
                  }
                }}
                aria-label={`${failed.name} ${failed.email} 詳細を表示`}
              >
                <td>{failed.name}</td>
                <td className="error-table__email-cell">{failed.email}</td>
                <td className="error-table__error-cell">{getFirstSentence(failed.errorContent)}</td>
                <td>{failed.sendTime}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={4} className="error-table__empty">
                エラーデータがありません
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default ErrorTable;
